<template>
  <div class="hotel-details-view p-6">
    <vs-card>
      <div class="flex flex-wrap items-center justify-between gap-3 mb-4 px-2">
        <vs-button type="border" @click="goBack">{{ backLabel }}</vs-button>
        <div class="flex flex-wrap gap-2">
          <vs-button type="border" @click="goDashboard">Hotel dashboard</vs-button>
          <vs-button v-if="showEditButton" color="primary" @click="goEdit">Edit hotel</vs-button>
        </div>
      </div>

      <div v-if="loading" class="py-16 text-center text-lg">Loading…</div>

      <div v-else-if="!hotel" class="py-16 text-center text-danger">Hotel not found or you do not have access.</div>

      <div v-else>
        <div class="hotel-details-header">
          <h2 class="hotel-details-title">{{ displayName }}</h2>
          <p v-if="gurudwaraName" class="hotel-details-sub">Gurudwara: {{ gurudwaraName }}</p>
          <div class="hotel-details-chips">
            <vs-chip color="primary" v-if="hotel.is_draft" class="hotel-details-chip-pad">Draft</vs-chip>
            <vs-chip :color="hotel.is_active ? 'success' : 'warning'" class="hotel-details-chip-pad">{{ hotel.is_active ? 'Active' : 'Inactive' }}</vs-chip>
            <vs-chip :color="hotel.is_verified ? 'success' : 'dark'" class="hotel-details-chip-pad">{{ hotel.is_verified ? 'Verified' : 'Not verified' }}</vs-chip>
          </div>
        </div>

        <vs-tabs v-model="activeTab" position="top" alignment="fixed" class="hotel-detail-tabs">
          <vs-tab label="Hotel Details">
            <div class="hotel-tab-panel">
              <div class="hd-panel">
                <h3 class="hd-panel-title">Basic details</h3>
                <div class="vx-row">
                  <div class="vx-col md:w-1/2 w-full mb-4 hd-field">
                    <span class="hd-field-label">Property name</span>
                    <p class="hd-field-value">{{ displayName || '—' }}</p>
                  </div>
                  <div v-if="canSeePlatformFee" class="vx-col md:w-1/2 w-full mb-4 hd-field">
                    <span class="hd-field-label">Sangat platform fee</span>
                    <p class="hd-field-value">{{ hotel.platform_fee != null ? hotel.platform_fee + '%' : '—' }}</p>
                  </div>
                </div>
              </div>

              <div class="hd-panel">
                <h3 class="hd-panel-title">Property images</h3>
                <p v-if="!hotel.images || !hotel.images.length" class="hd-empty">No images uploaded.</p>
                <div v-else class="hd-gallery">
                  <a v-for="(img, idx) in hotel.images" :key="'pi-' + idx" :href="img" target="_blank" rel="noopener" class="hd-gallery-item">
                    <img :src="img" alt="" />
                  </a>
                </div>
              </div>

              <div class="hd-panel">
                <h3 class="hd-panel-title">About property</h3>
                <p class="hd-prose">{{ hotel.description || '—' }}</p>
              </div>

              <div class="hd-panel">
                <h3 class="hd-panel-title">Amenities</h3>
                <div class="vx-row">
                  <div class="vx-col md:w-1/2 w-full mb-4 hd-field">
                    <span class="hd-field-label">Free Wi‑Fi</span>
                    <p class="hd-field-value">{{ hotel.free_wifi ? 'Yes' : 'No' }}</p>
                  </div>
                  <div class="vx-col md:w-1/2 w-full mb-4 hd-field">
                    <span class="hd-field-label">Parking</span>
                    <p class="hd-field-value">{{ hotel.parking ? 'Yes' : 'No' }}</p>
                  </div>
                  <div class="vx-col w-full hd-field">
                    <span class="hd-field-label">Payment terms</span>
                    <p class="hd-prose hd-prose-tight">{{ hotel.amenities || '—' }}</p>
                  </div>
                </div>
              </div>

              <div class="hd-panel">
                <h3 class="hd-panel-title">Opening hours</h3>
                <div class="hd-badge-row">
                  <span class="hd-mini-label">24 hours open</span>
                  <span class="hd-badge-answer">{{ hotel.is24hOpen ? 'Yes' : 'No' }}</span>
                </div>
                <div v-if="hotel.opening_hours && hotel.opening_hours.length" class="hd-table-wrap">
                  <vs-table :data="hotel.opening_hours" class="hd-table">
                    <template slot="thead">
                      <vs-th>Day</vs-th>
                      <vs-th>Open</vs-th>
                      <vs-th>Close</vs-th>
                    </template>
                    <template slot-scope="{ data }">
                      <vs-tr :key="i" v-for="(tr, i) in data">
                        <vs-td>{{ tr.day }}</vs-td>
                        <vs-td>{{ formatTime(tr.open_time) }}</vs-td>
                        <vs-td>{{ formatTime(tr.close_time) }}</vs-td>
                      </vs-tr>
                    </template>
                  </vs-table>
                </div>
                <p v-else class="hd-empty">No schedule added.</p>
              </div>

              <div class="hd-panel">
                <h3 class="hd-panel-title">Social media</h3>
                <ul class="hd-link-list">
                  <li v-for="item in socialEntries" :key="item.key" class="hd-link-row">
                    <span class="hd-field-label hd-capitalize">{{ item.key }}</span>
                    <a v-if="item.value" :href="normalizeUrl(item.value)" target="_blank" rel="noopener" class="hd-link">{{ item.value }}</a>
                    <span v-else class="hd-empty-inline">—</span>
                  </li>
                </ul>
              </div>

              <div class="hd-panel">
                <h3 class="hd-panel-title">Other links</h3>
                <p v-if="!hotel.custom_link || !hotel.custom_link.length" class="hd-empty">None</p>
                <ul v-else class="hd-link-list">
                  <li v-for="(link, i) in hotel.custom_link" :key="'cl-' + i" class="hd-link-row">
                    <span class="hd-field-value">{{ link.title || 'Link' }}</span>
                    <a v-if="link.link" :href="normalizeUrl(link.link)" target="_blank" rel="noopener" class="hd-link">{{ link.link }}</a>
                  </li>
                </ul>
              </div>

              <div class="hd-panel">
                <h3 class="hd-panel-title">Other names</h3>
                <p v-if="!hotel.other_name || !hotel.other_name.length" class="hd-empty">None</p>
                <ul v-else class="hd-bullet-list">
                  <li v-for="(n, i) in hotel.other_name" :key="'on-' + i">{{ n }}</li>
                </ul>
              </div>

              <div class="hd-panel hd-panel-last">
                <h3 class="hd-panel-title">YouTube handles</h3>
                <p v-if="!hotel.youtube_handle_name || !hotel.youtube_handle_name.length" class="hd-empty">None</p>
                <ul v-else class="hd-bullet-list">
                  <li v-for="(h, i) in hotel.youtube_handle_name" :key="'yt-' + i">{{ h }}</li>
                </ul>
              </div>
            </div>
          </vs-tab>

          <vs-tab label="Rooms">
            <div class="hotel-tab-panel">
              <p v-if="!hotel.rooms || !hotel.rooms.length" class="hd-empty hd-empty-padded">No rooms configured.</p>
              <div v-else>
                <div v-for="(room, ri) in hotel.rooms" :key="'room-' + ri" class="hd-room-card">
                  <div class="hd-room-head">
                    <span class="hd-room-title">{{ room.room_type || room.name || 'Room' }}</span>
                  </div>
                  <div class="hd-room-body">
                    <div class="vx-row">
                      <div class="vx-col w-full md:w-1/2 mb-4 hd-field">
                        <span class="hd-field-label">Your charge / night</span>
                        <p class="hd-field-value hd-price">₹{{ room.price_per_night || room.your_charge || 0 }}</p>
                      </div>
                      <div class="vx-col w-full md:w-1/2 mb-4 hd-field" v-if="room.number_of_rooms">
                        <span class="hd-field-label">Number of rooms</span>
                        <p class="hd-field-value">{{ room.number_of_rooms }}</p>
                      </div>
                      <div class="vx-col w-full mb-4 hd-field" v-if="room.description">
                        <span class="hd-field-label">Description</span>
                        <p class="hd-prose hd-prose-tight">{{ room.description }}</p>
                      </div>
                      <div class="vx-col w-full mb-4" v-if="room.images && room.images.length">
                        <span class="hd-field-label">Photos</span>
                        <div class="hd-room-thumbs">
                          <a v-for="(im, ii) in room.images" :key="'rim-' + ri + '-' + ii" :href="im" target="_blank" rel="noopener" class="hd-thumb">
                            <img :src="im" alt="" />
                          </a>
                        </div>
                      </div>
                      <div class="vx-col w-full" v-if="room.options && room.options.length">
                        <span class="hd-field-label">Options</span>
                        <ul class="hd-option-list">
                          <li v-for="(opt, oi) in room.options" :key="'opt-' + ri + '-' + oi">
                            <span class="hd-opt-name">{{ opt.name || 'Option' }}</span>
                            <span class="hd-opt-price">₹{{ opt.price_per_night || opt.your_charge || 0 }}</span>
                            <span v-if="opt.capacity" class="hd-opt-cap">({{ opt.capacity }} guests)</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </vs-tab>

          <vs-tab label="Terms, Rules & Policies">
            <div class="hotel-tab-panel">
              <div class="hd-policy">
                <h4 class="hd-policy-title">Hotel rules</h4>
                <p class="hd-prose">{{ hotel.hotel_rules || '—' }}</p>
              </div>
              <div class="hd-policy">
                <h4 class="hd-policy-title">Cancellation policy</h4>
                <p class="hd-prose">{{ hotel.cancellation_policy || '—' }}</p>
              </div>
              <div class="hd-policy">
                <h4 class="hd-policy-title">Safety information</h4>
                <p class="hd-prose">{{ hotel.safety_information || '—' }}</p>
              </div>
              <div class="hd-policy hd-policy-last">
                <h4 class="hd-policy-title">Refund policy</h4>
                <p class="hd-prose">{{ hotel.refund_policy || '—' }}</p>
              </div>
            </div>
          </vs-tab>

          <vs-tab label="Collect documents">
            <div class="hotel-tab-panel">
              <div class="hd-panel">
                <h3 class="hd-panel-title">Document collection</h3>
                <div class="hd-badge-row">
                  <span class="hd-mini-label">Enabled</span>
                  <span class="hd-badge-answer">{{ hotel.is_documentCollect ? 'Yes' : 'No' }}</span>
                </div>
                <div v-if="collectDocs.length" class="hd-table-wrap hd-table-wrap-mt">
                  <vs-table :data="collectDocs" class="hd-table">
                    <template slot="thead">
                      <vs-th>Document</vs-th>
                      <vs-th>Mandatory</vs-th>
                      <vs-th>Hint</vs-th>
                    </template>
                    <template slot-scope="{ data }">
                      <vs-tr :key="i" v-for="(tr, i) in data">
                        <vs-td>{{ tr.name }}</vs-td>
                        <vs-td>{{ tr.mandatory ? 'Yes' : 'No' }}</vs-td>
                        <vs-td>{{ tr.hint || '—' }}</vs-td>
                      </vs-tr>
                    </template>
                  </vs-table>
                </div>
                <p v-else class="hd-empty">No documents configured.</p>
              </div>
            </div>
          </vs-tab>
        </vs-tabs>
      </div>
    </vs-card>
  </div>
</template>

<script>
import axios from '@/axios'
import moment from 'moment'

export default {
  name: 'HotelDetailsView',
  data() {
    return {
      loading: true,
      hotel: null,
      activeTab: 0
    }
  },
  computed: {
    hotelId() {
      return this.$route.params.hotelId
    },
    gurudwaraRouteId() {
      return this.$route.params.gurudwaraId
    },
    displayName() {
      if (!this.hotel) return ''
      return this.hotel.property_name || this.hotel.name || ''
    },
    gurudwaraName() {
      if (!this.hotel || !this.hotel.gurudwara) return ''
      const g = this.hotel.gurudwara
      return typeof g === 'object' && g !== null ? g.name : ''
    },
    isHotelManager() {
      const u = this.$store.state.auth.userInfo
      const slug = (u && u.role && (u.role.slug || u.role.role_name)) || ''
      return String(slug)
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '-') === 'hotel-manager'
    },
    canSeePlatformFee() {
      return !this.isHotelManager
    },
    showEditButton() {
      if (!this.hotel) return false
      if (this.isHotelManager && this.hotel.is_draft) return false
      return true
    },
    backLabel() {
      return this.gurudwaraRouteId ? 'Back to Gurudwara' : 'Back to hotel list'
    },
    socialEntries() {
      const sm = (this.hotel && this.hotel.social_media) || {}
      const keys = ['facebook', 'instagram', 'twitter', 'snapchat', 'tiktok', 'linkedin', 'youtube']
      return keys.map((key) => ({ key, value: sm[key] && String(sm[key]).trim() ? sm[key] : '' }))
    },
    collectDocs() {
      if (!this.hotel || !Array.isArray(this.hotel.collect_documents)) return []
      return this.hotel.collect_documents.map((doc) => ({
        name: doc.document_name || doc.name || '—',
        mandatory: doc.is_required !== undefined && doc.is_required !== null ? doc.is_required : !!doc.mandatory,
        hint: doc.description || doc.hint || ''
      }))
    }
  },
  mounted() {
    this.fetchHotel()
  },
  methods: {
    normalizeUrl(val) {
      if (!val) return '#'
      const s = String(val)
      if (s.startsWith('http://') || s.startsWith('https://')) return s
      return `https://${s}`
    },
    formatTime(t) {
      if (!t) return '—'
      return moment(t).local().format('hh:mm A')
    },
    async fetchHotel() {
      this.loading = true
      this.hotel = null
      try {
        const { data } = await axios.get(`/admin/hotel/${this.hotelId}`)
        if (data.success) {
          this.hotel = data.data
        }
      } catch (e) {
        this.hotel = null
        this.$vs.notify({
          title: 'Error',
          text: (e.response && e.response.data && e.response.data.message) || 'Failed to load hotel',
          color: 'danger'
        })
      } finally {
        this.loading = false
      }
    },
    goBack() {
      if (this.gurudwaraRouteId) {
        this.$router.push(`/gurudwara/${this.gurudwaraRouteId}/edit`)
      } else {
        this.$router.push('/hotel/list')
      }
    },
    goEdit() {
      if (this.gurudwaraRouteId) {
        this.$router.push(`/gurudwara/${this.gurudwaraRouteId}/hotel/${this.hotelId}/edit`)
      } else {
        this.$router.push(`/hotel/${this.hotelId}/edit`)
      }
    },
    goDashboard() {
      if (!this.hotelId) return
      if (this.gurudwaraRouteId) {
        this.$router.push(`/gurudwara/${this.gurudwaraRouteId}/hotel/${this.hotelId}/dashboard`)
      } else {
        this.$router.push(`/hotel/${this.hotelId}/dashboard`)
      }
    }
  }
}
</script>

<style scoped>
.hotel-details-view {
  min-height: 100vh;
  background: #f0efee;
}

.hotel-details-header {
  padding: 0.5rem 0.25rem 1.25rem;
  margin-bottom: 0.25rem;
  display: flex;
  align-items: center;
  gap:20px
}

.hotel-details-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #111;
  margin: 0 0 0.35rem;
  line-height: 1.3;
}

.hotel-details-sub {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0 0 0.75rem;
}

.hotel-details-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

/* 4px vertical, 16px horizontal padding on status chips */
.hotel-details-view >>> .hotel-details-chips .hotel-details-chip-pad.con-vs-chip,
.hotel-details-view >>> .hotel-details-chips .hotel-details-chip-pad {
  padding: 4px 16px !important;
}

.hotel-detail-tabs {
  margin-top: 0;
}

/* Tab bar: readable labels, subtle separation from content */
.hotel-details-view >>> .hotel-detail-tabs .con-ul-tabs {
  padding: 0 4px;
}

.hotel-details-view >>> .hotel-detail-tabs .ul-tabs.vs-tabs--ul.ul-tabs-fixed {
  height: 44px;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.hotel-details-view >>> .hotel-detail-tabs .ul-tabs.vs-tabs--ul.ul-tabs-fixed::-webkit-scrollbar {
  display: none;
}

/* Tab underline only — never on the vs-tabs root */
.hotel-details-view >>> .hotel-detail-tabs .line-vs-tabs {
  height: 4px !important;
}

/* Tab body: padded panel area */
.hotel-tab-panel {
  /* background: #fafafa; */
  /* padding: 1.25rem 1rem 2rem; */
  min-height: 280px;
}


.hd-panel {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  padding: 1.25rem 1.25rem 1.35rem;
  margin-bottom: 1rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.hd-panel-last {
  margin-bottom: 0;
}

.hd-panel-title {
  font-size: 1.125rem;
  font-weight: 700;
  color: #111;
  margin: 0 0 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #f0efee;
}

.hd-field-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b7280;
  margin-bottom: 0.35rem;
}

.hd-capitalize {
  text-transform: capitalize;
  letter-spacing: 0;
  font-size: 0.8125rem;
}

.hd-field-value {
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  word-break: break-word;
}

.hd-price {
  font-size: 1.125rem;
}

.hd-empty {
  margin: 0;
  font-size: 0.9375rem;
  color: #9ca3af;
}

.hd-empty-padded {
  padding: 2rem 1rem;
  text-align: center;
}

.hd-empty-inline {
  color: #d1d5db;
  font-size: 0.9375rem;
}

.hd-prose {
  margin: 0;
  font-size: 0.9375rem;
  line-height: 1.6;
  color: #374151;
  white-space: pre-wrap;
}

.hd-prose-tight {
  margin-top: 0.25rem;
}

.hd-gallery {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.hd-gallery-item {
  display: block;
  width: 7.5rem;
  height: 7.5rem;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  flex-shrink: 0;
  background: #f9fafb;
}

.hd-gallery-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  vertical-align: middle;
}

.hd-badge-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.hd-mini-label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #4b5563;
}

.hd-badge-answer {
  display: inline-block;
  font-size: 0.8125rem;
  font-weight: 700;
  padding: 0.2rem 0.65rem;
  border-radius: 6px;
  background: #f0efee;
  color: #111;
}

.hd-table-wrap {
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #e8e8e8;
}

.hd-table-wrap-mt {
  margin-top: 1rem;
}

.hotel-details-view >>> .hd-table .vs-table--tbody-table {
  border-collapse: collapse;
}

.hd-link-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.hd-link-row {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.65rem 0;
  border-bottom: 1px solid #f3f4f6;
}

.hd-link-row:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

@media (min-width: 640px) {
  .hd-link-row {
    flex-direction: row;
    align-items: baseline;
    gap: 1rem;
  }

  .hd-link-row .hd-field-label {
    flex: 0 0 7rem;
    margin-bottom: 0;
  }
}

.hd-link {
  font-size: 0.9375rem;
  color: #0b1444;
  text-decoration: underline;
  word-break: break-all;
}

.hd-bullet-list {
  margin: 0;
  padding-left: 1.25rem;
  color: #374151;
  font-size: 0.9375rem;
  line-height: 1.7;
}

/* Rooms */
.hd-room-card {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  margin-bottom: 1rem;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.hd-room-card:last-child {
  margin-bottom: 0;
}

.hd-room-head {
  background: #f8f8f8;
  border-bottom: 1px solid #e8e8e8;
  padding: 0.85rem 1.25rem;
}

.hd-room-title {
  font-size: 1.0625rem;
  font-weight: 700;
  color: #111;
}

.hd-room-body {
  padding: 1.25rem;
}

.hd-room-thumbs {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.35rem;
}

.hd-thumb {
  display: block;
  width: 5rem;
  height: 5rem;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
}

.hd-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.hd-option-list {
  list-style: none;
  margin: 0.5rem 0 0;
  padding: 0;
}

.hd-option-list li {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.35rem 0.75rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f3f4f6;
  font-size: 0.9375rem;
}

.hd-option-list li:last-child {
  border-bottom: none;
}

.hd-opt-name {
  font-weight: 600;
  color: #111;
}

.hd-opt-price {
  font-weight: 700;
  color: #1f2937;
}

.hd-opt-cap {
  font-size: 0.8125rem;
  color: #6b7280;
}

/* Terms */
.hd-policy {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  padding: 1.15rem 1.25rem 1.25rem;
  margin-bottom: 1rem;
}

.hd-policy-last {
  margin-bottom: 0;
}

.hd-policy-title {
  font-size: 0.8125rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
  margin: 0 0 0.65rem;
}

.hotel-details-view >>> .vs-card {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}
</style>
